import { Fragment, useCallback, useMemo, useState, type FC } from 'react';
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import { useSessionsStore } from '@/store/sessions.ts';
import { useFiltersStore } from '@/store/filters.ts';
import { SessionItem } from './SessionItem.tsx';
import { useUnstableSheetScrollContext } from '@pacevault/ui/components/pvBottomSheet/PvBottomSheetProvider.tsx';
import { matchesFilters } from '@/lib/filtering/filters.ts';

const ESTIMATED_ROW_SIZE = 82;

export const SessionList: FC = () => {
  const sessions = useSessionsStore((s) => s.sessions);
  const activeFilter = useFiltersStore((s) => s.activeFilter);
  const sheetScroller = useUnstableSheetScrollContext()?.element ?? null;
  const [scrollMargin, setScrollMargin] = useState(0);
  const listRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      let top = node.offsetTop;
      if (sheetScroller) {
        top =
          node.getBoundingClientRect().top -
          sheetScroller.getBoundingClientRect().top +
          sheetScroller.scrollTop;
      }
      setScrollMargin((prev) => (prev === top ? prev : top));
    },
    [sheetScroller],
  );

  const filtered = useMemo(() => {
    const now = Date.now();
    return sessions
      .filter((s) => !s.isPlanned && matchesFilters({ session: s, criteria: activeFilter, now }))
      .sort((a, b) => b.date - a.date);
  }, [sessions, activeFilter]);

  const windowVirtualizer = useWindowVirtualizer({
    count: filtered.length,
    estimateSize: () => ESTIMATED_ROW_SIZE,
    overscan: 5,
    scrollMargin,
    enabled: !sheetScroller,
  });
  const sheetVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => sheetScroller,
    estimateSize: () => ESTIMATED_ROW_SIZE,
    overscan: 5,
    scrollMargin,
    enabled: !!sheetScroller,
  });
  const virtualizer = sheetScroller ? sheetVirtualizer : windowVirtualizer;

  return (
    <div ref={listRef} className="relative" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const session = filtered[virtualRow.index];
        if (!session) {
          return <Fragment key={virtualRow.index} />;
        }

        return (
          <div
            key={session.id}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            className="absolute top-0 left-0 w-full"
            style={{
              transform: `translateY(${virtualRow.start - scrollMargin}px)`,
            }}
          >
            <div className="pb-2">
              <SessionItem session={session} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
